import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, AlertCircle } from "lucide-react";

export default function ManageSubscription() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [sub, setSub] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    if (!token) { setError("Lien de gestion invalide"); return; }
    api.get(`/subscriptions/manage?token=${encodeURIComponent(token)}`)
      .then(r => setSub(r.data))
      .catch(e => setError(e.response?.data?.detail || "Impossible de charger ton abonnement"));
  }, [token]);

  async function doCancel() {
    setCanceling(true);
    try {
      await api.post("/subscriptions/cancel", { token });
      toast.success("Résiliation enregistrée");
      const r = await api.get(`/subscriptions/manage?token=${encodeURIComponent(token)}`);
      setSub(r.data);
      setConfirming(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setCanceling(false); }
  }

  if (error) return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <XCircle size={48} className="mx-auto text-[#A86962] mb-4"/>
      <h1 className="font-serif text-3xl mb-4">Oups</h1>
      <p className="text-[#4A4036]">{error}</p>
    </div>
  );
  if (!sub) return <div className="max-w-xl mx-auto px-6 py-20 text-center">Chargement…</div>;

  const status = sub.status;
  const statusLabel = status === "active" ? "Actif" : status === "cancel_scheduled" ? "Résiliation programmée" : status === "canceled" ? "Résilié" : status;
  const statusColor = status === "active" ? "bg-[#7B8A72] text-white" : status === "cancel_scheduled" ? "bg-[#E8DCC8] text-[#A86962]" : "bg-[#DCCBBA]";
  const periodEnd = sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString("fr-FR") : "—";
  const minCancel = sub.min_cancel_at ? new Date(sub.min_cancel_at).toLocaleDateString("fr-FR") : "—";

  return (
    <div data-testid="manage-subscription" className="max-w-3xl mx-auto px-6 md:px-12 py-16">
      <p className="font-hand text-2xl text-[#A3876A] mb-4">Ton espace</p>
      <h1 className="font-serif text-5xl mb-10">Mon abonnement</h1>

      <div className="bg-[#F5F0E6] p-8 border border-[#E8DCC8] mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-[#7C7064]">Titulaire</p>
            <p className="font-serif text-xl">{sub.customer_name || "—"}</p>
            <p className="text-sm text-[#7C7064]">{sub.email}</p>
          </div>
          <span data-testid="sub-status" className={`px-3 py-1 text-xs uppercase tracking-widest ${statusColor}`}>{statusLabel}</span>
        </div>

        <div className="grid md:grid-cols-3 gap-6 border-t border-[#E8DCC8] pt-6">
          <div>
            <p className="text-xs text-[#7C7064] uppercase tracking-widest mb-2">Mois versés</p>
            <p className="font-serif text-3xl">{sub.paid_invoices_count} <span className="text-sm text-[#7C7064]">/ {sub.min_invoices} min.</span></p>
          </div>
          <div>
            <p className="text-xs text-[#7C7064] uppercase tracking-widest mb-2">Prochain prélèvement</p>
            <p className="font-serif text-xl">{status === "active" ? periodEnd : "—"}</p>
          </div>
          <div>
            <p className="text-xs text-[#7C7064] uppercase tracking-widest mb-2">Résiliable à partir du</p>
            <p className="font-serif text-xl">{minCancel}</p>
          </div>
        </div>
      </div>

      {/* Codes issued */}
      {sub.codes_issued && sub.codes_issued.length > 0 && (
        <div className="mb-8">
          <h2 className="font-serif text-2xl mb-4">Tes codes correspondance</h2>
          <div className="bg-[#FDFBF7] border border-[#E8DCC8]">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F0E6] text-left"><tr>
                <th className="p-3">Code</th><th className="p-3">Émis</th><th className="p-3">Expire</th>
              </tr></thead>
              <tbody>
                {sub.codes_issued.slice().reverse().map(c => (
                  <tr key={c.code} className="border-t border-[#E8DCC8]">
                    <td className="p-3 font-mono">{c.code}</td>
                    <td className="p-3 text-xs text-[#7C7064]">{new Date(c.issued_at).toLocaleDateString("fr-FR")}</td>
                    <td className="p-3 text-xs text-[#7C7064]">{new Date(c.expires_at).toLocaleDateString("fr-FR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#7C7064] mt-2">Chaque code est utilisable une seule fois pendant sa durée de validité (60 jours).</p>
        </div>
      )}

      {/* Actions */}
      <div className="bg-[#FDFBF7] p-8 border border-[#E8DCC8]">
        <h2 className="font-serif text-2xl mb-4">Résiliation</h2>
        {status === "canceled" ? (
          <p className="text-[#4A4036] flex items-center gap-2"><CheckCircle2 size={16}/> Ton abonnement est résilié.</p>
        ) : status === "cancel_scheduled" ? (
          <div className="text-[#4A4036]">
            <p className="flex items-center gap-2 mb-2"><Clock size={16} className="text-[#A3876A]"/> Résiliation programmée</p>
            <p className="text-sm text-[#7C7064]">Tu ne seras plus prélevée à partir du {periodEnd}. Tu conserves tes codes en cours de validité.</p>
          </div>
        ) : !sub.can_cancel ? (
          <div className="text-[#4A4036]">
            <p className="flex items-start gap-2 mb-2"><AlertCircle size={16} className="text-[#A3876A] mt-1"/> Tu pourras résilier après {sub.min_invoices} versements — soit après le <strong>{minCancel}</strong>.</p>
            <p className="text-sm text-[#7C7064]">C'est notre façon de nous engager mutuellement dans la durée. Après cette période, la résiliation se fait ici en un clic.</p>
          </div>
        ) : !confirming ? (
          <div>
            <p className="text-[#4A4036] mb-6">Tu peux résilier à tout moment. Tu continueras à recevoir ta correspondance jusqu'à la fin de la période en cours ({periodEnd}).</p>
            <button data-testid="cancel-btn" onClick={()=>setConfirming(true)} className="btn-outline">Résilier mon abonnement</button>
          </div>
        ) : (
          <div>
            <p className="text-[#4A4036] mb-6"><strong>Tu es sûre ?</strong> Tu ne seras plus prélevée à partir du {periodEnd}.</p>
            <div className="flex gap-3">
              <button data-testid="cancel-confirm" onClick={doCancel} disabled={canceling} className="btn-primary">{canceling ? "…" : "Oui, résilier"}</button>
              <button onClick={()=>setConfirming(false)} className="btn-outline">Annuler</button>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-[#7C7064] mt-8 text-center">
        Une question ? <Link to="/contact" className="underline">Écris-moi</Link>.
      </p>
    </div>
  );
}
